// Apolo IDE — sessionKind (E5 provenance polish, sprint hermes-harvest).
//
// SINGLE shared helper for Local/Cloud provenance in the Code UI. Before this
// existed, ComposerFooterStrip and CodeSidebar each carried their own inline
// copy of "derive cloud/local from the model's provider". They drifted in
// spelling (`LOCAL_PROVIDERS.has(prov)` vs `prov === 'ollama' || ...`) while
// producing the same output. This consolidates them WITHOUT changing what
// either renders.
//
// Contract (preserve exactly — call sites depend on it):
//   - providerOf(model) unknown  → sessionKind returns { kind: null } (callers
//     treat as "no chrome"); isLocalProvider returns false.
//   - provider in LOCAL_PROVIDERS → { kind: 'local', Icon: Cpu,  label: 'local' }
//   - any other known provider   → { kind: 'cloud', Icon: Cloud, label: 'cloud' }
//
// Icons mirror the lucide ones the two call sites already used (Cpu / Cloud).
// PURE except for the read-only providerOf import — no I/O, no React state.

import { Cloud, Cpu } from 'lucide-react';
import { providerOf } from '../modelRegistry.js';

// Providers that run on the user's own machine (no cloud round-trip). Mirrors
// the Set that ComposerFooterStrip carried inline.
export const LOCAL_PROVIDERS = new Set(['ollama', 'lmstudio']);

/**
 * True when the model's provider runs locally. Unknown/unmapped models → false
 * (matches both old call sites: providerOf undefined ⇒ not local).
 * @param {string} model provider-prefixed model id
 * @returns {boolean}
 */
export function isLocalProvider(model) {
  const prov = providerOf(model);
  if (!prov) return false;
  return LOCAL_PROVIDERS.has(prov);
}

/**
 * Provenance descriptor for a model. Returns the {kind, Icon, label} both the
 * composer strip and the sidebar rows consume. Unknown model ⇒ kind null and
 * no icon, so callers stay silent (they render nothing) — preserving the
 * "don't invent chrome for unmapped models" behavior exactly.
 * @param {string} model provider-prefixed model id
 * @returns {{ kind: 'local'|'cloud'|null, Icon: any, label: string|null }}
 */
export function sessionKind(model) {
  const prov = providerOf(model);
  if (!prov) return { kind: null, Icon: null, label: null };
  const local = LOCAL_PROVIDERS.has(prov);
  return local
    ? { kind: 'local', Icon: Cpu, label: 'local' }
    : { kind: 'cloud', Icon: Cloud, label: 'cloud' };
}
